import { supabase } from '../lib/supabase';
import { assertContentIsAllowed } from '../lib/contentModeration';

export type MessageAttachmentType = 'image' | 'file';

export interface MessageAttachmentMeta {
  type: MessageAttachmentType;
  name: string;
  url: string;
  size: number;
}

export interface MessagePayload {
  text: string;
  attachments: MessageAttachmentMeta[];
}

const DEFAULT_PAYLOAD: MessagePayload = {
  text: '',
  attachments: [],
};

const getMessagesBucket = () => 'message_attachments';

const generateFilePath = (userId: string, file: File): string => {
  const fileExt = file.name.split('.').pop();
  const sanitizedExt = fileExt ? fileExt.replace(/[^a-zA-Z0-9]/g, '') : 'dat';
  const safeBaseName = file.name
    .split('.')
    .slice(0, -1)
    .join('.')
    .replace(/[^a-zA-Z0-9-_]/g, '')
    .slice(0, 40);
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 8);
  return `${userId}/${timestamp}-${random}-${safeBaseName || 'file'}.${sanitizedExt}`;
};

const uploadAttachment = async (file: File): Promise<MessageAttachmentMeta> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const filePath = generateFilePath(user.id, file);
  const bucket = getMessagesBucket();

  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(filePath);

  const isImage = file.type.startsWith('image/');

  return {
    type: isImage ? 'image' : 'file',
    name: file.name,
    url: data.publicUrl,
    size: file.size,
  };
};

export const serializeMessagePayload = (payload: MessagePayload): string => {
  return JSON.stringify({
    text: payload.text ?? '',
    attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
  });
};

export const deserializeMessagePayload = (content: string | null | undefined): MessagePayload => {
  if (!content) return DEFAULT_PAYLOAD;

  try {
    const parsed = JSON.parse(content) as Record<string, unknown> | undefined;
    const text = typeof parsed?.text === 'string' ? parsed.text : content;
    const parsedAttachments = parsed?.attachments;
    const attachments = Array.isArray(parsedAttachments)
      ? parsedAttachments.filter((att: unknown) => {
          const a = att as Record<string, unknown>;
          return typeof a?.url === 'string' && typeof a?.name === 'string';
        })
      : [];

    return {
      text,
      attachments: attachments.map((att: unknown) => {
        const a = att as Record<string, unknown>;
        return {
          type: a.type === 'image' ? 'image' : 'file',
          name: a.name as string,
          url: a.url as string,
          size: typeof a.size === 'number' ? a.size : 0,
        };
      }),
    };
  } catch {
    return {
      text: content,
      attachments: [],
    };
  }
};

export const messagesService = {
  uploadAttachment,
  serializeMessagePayload,
  deserializeMessagePayload,

  async getConversations() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        participant_one:profiles!conversations_participant_one_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium
        ),
        participant_two:profiles!conversations_participant_two_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium
        ),
        messages (
          id,
          content,
          sender_id,
          is_read,
          created_at
        )
      `)
      .or(`participant_one_id.eq.${user.id},participant_two_id.eq.${user.id}`)
      .order('last_message_at', { ascending: false });

    if (error) throw error;

    return data?.map((conversation: Record<string, unknown>) => {
      const p1 = conversation.participant_one as Record<string, string>;
      const p2 = conversation.participant_two as Record<string, string>;
      const otherParticipant = p1.id === user.id ? p2 : p1;

      const messages = conversation.messages as Array<Record<string, unknown>> | undefined;
      const lastMessage = messages
        ?.sort((a, b) => new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime())[0];

      return {
        ...conversation,
        otherParticipant,
        lastMessage,
        unreadCount: messages?.filter((m) => !(m.is_read as boolean) && (m.sender_id as string) !== user.id).length || 0,
      };
    }) || [];
  },

  async getOrCreateConversation(otherUserId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: existing, error: existingError } = await supabase
      .from('conversations')
      .select('*')
      .or(
        `and(participant_one_id.eq.${user.id},participant_two_id.eq.${otherUserId}),and(participant_one_id.eq.${otherUserId},participant_two_id.eq.${user.id})`
      )
      .single();

    if (existingError && existingError.code !== 'PGRST116') throw existingError;

    if (existing) {
      return existing.id;
    }

    const { data, error } = await supabase
      .from('conversations')
      .insert({
        participant_one_id: user.id,
        participant_two_id: otherUserId,
      })
      .select()
      .single();

    if (error) throw error;
    return data.id;
  },

  async getMessages(conversationId: string) {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  async sendMessage(conversationId: string, payload: MessagePayload) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    await assertContentIsAllowed([payload.text]);

    const content = serializeMessagePayload(payload);

    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: user.id,
        content,
      })
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey (
          id,
          username,
          full_name,
          avatar_url
        )
      `)
      .single();

    if (error) throw error;
    return data;
  },

  async markMessagesAsRead(conversationId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('conversation_id', conversationId)
      .neq('sender_id', user.id);

    if (error) throw error;
  },

  async deleteConversation(conversationId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: conversation, error: convError } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .single();

    if (convError) throw new Error('Conversación no encontrada');

    if (conversation.participant_one_id !== user.id && conversation.participant_two_id !== user.id) {
      throw new Error('No tienes permisos para eliminar esta conversación');
    }

    const { error: messagesError } = await supabase
      .from('messages')
      .delete()
      .eq('conversation_id', conversationId);

    if (messagesError) {
      console.error('Error deleting messages:', messagesError);
      throw new Error('Error al eliminar mensajes: ' + messagesError.message);
    }

    const { error } = await supabase
      .from('conversations')
      .delete()
      .eq('id', conversationId);

    if (error) {
      console.error('Error deleting conversation:', error);
      throw new Error('Error al eliminar conversación: ' + error.message);
    }

    return true;
  },

  async markMessageAsSpam(messageId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('messages')
      .update({ status: 'spam' })
      .eq('id', messageId)
      .select()
      .single();

    if (error) {
      console.error('Error marking message as spam:', error);
      throw new Error('Error al marcar como spam: ' + error.message);
    }
    return data;
  },

  subscribeToMessages(conversationId: string, callback: (message: Record<string, unknown>) => void) {
    return supabase
      .channel(`messages:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          callback(payload.new);
        }
      )
      .subscribe();
  },
};
